import { createSelector } from '@reduxjs/toolkit'

import { ApplicationState } from 'redux/Stores/types'
import type { TAttributeOptionDto } from '@homepal/api-contract'

import { GroupedAttributeOptions } from 'types/GlobalAttributeOptions'
import { KpiOptionObject } from 'types/GlobalKpiOption'
import {
  TWidget,
  TWidgetSettings,
  WidgetType,
  isWidgetSettingsLoaded
} from 'types/GlobalWidget'

// select fetched status
export const selectFetched = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.AttributeOptionStore.fetched
  ],
  (fetched) => fetched
)

// select all attribute options
export const selectAll = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.AttributeOptionStore.data
  ],
  (data) => data
)

export const selectByFactAndLoading = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) =>
      state.AttributeOptionStore.optionsByFactTableId,
    (state: ApplicationState) => state.AttributeOptionStore.fetched
  ],
  (
    data,
    fetched
  ): [
    {
      [factTableId: string]: TAttributeOptionDto[]
    },
    boolean
  ] => [data, !fetched]
)

// select all group by fact table
export const selectAllGrouped = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.AttributeOptionStore.optionsByFactTableId
  ],
  (optionsByFactTableId) => {
    return Object.entries(optionsByFactTableId).reduce<GroupedAttributeOptions>(
      (acc, [factTableId, options]) => {
        acc[factTableId] = {
          segment: options.filter((option) => option.segment_attribute),
          breakdown: options.filter((option) => option.segment_attribute),
          filter: options.filter((option) => option.filter_attribute)
        }

        return acc
      },
      {}
    )
  }
)

function itemsIncludedInAllLists(lists: string[][]): Record<string, string> {
  // Build a frequency map for each item across all lists
  const frequency = lists.reduce<Record<string, number>>((acc, list) => {
    list.forEach((item) => {
      acc[item] = (acc[item] || 0) + 1
    })

    return acc
  }, {})

  // Return an object of items that appear in every list
  return Object.keys(frequency).reduce<Record<string, string>>(
    (commonItems, item) => {
      if (frequency[item] === lists.length) {
        commonItems[item] = item
      }

      return commonItems
    },
    {}
  )
}

/**
 * Validate attribute option based on widget type
 * and attribute option type.
 */
const validateAttributeOption = (
  option: TAttributeOptionDto,
  type: 'segment_attribute' | 'breakdown_attribute' | 'filter_attribute',
  widgetType: WidgetType
) => {
  // breakdown reuses the segment flag; only filter has its own
  const baseAttribute =
    type === 'filter_attribute' ? 'filter_attribute' : 'segment_attribute'
  const allow = option[baseAttribute] && !option.disabled

  if (widgetType === WidgetType.LINE_CHART && type === 'segment_attribute') {
    // line chart is required to be time series
    return allow && option.is_time_series
  }
  if (widgetType === WidgetType.LINE_CHART && type === 'breakdown_attribute') {
    // line chart cant breakdown on calendar
    return allow && option.type !== 'DATE'
  }

  if (widgetType === WidgetType.FUNNEL) {
    return allow && option.type !== 'DATE'
  }

  return allow
}

export const selectCommonAttributeOptions = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) =>
      state.AttributeOptionStore.optionsByFactTableId,
    (_: ApplicationState, widget: TWidget) => widget,
    (
      _: ApplicationState,
      widget: TWidget,
      type: 'segment_attribute' | 'filter_attribute'
    ) => type
  ],
  (optionsByFactTableId, widget, type) => {
    // Type guard - return empty if settings not loaded
    if (!isWidgetSettingsLoaded(widget)) {
      return []
    }

    const settings = widget.settings

    if (settings.kpi_options.length === 0) {
      return []
    }

    // get options for main kpi option
    const mainKpiOptionId = settings.kpi_options.find(
      (item) => item.index === 0
    ) as KpiOptionObject
    const attributeOptions =
      optionsByFactTableId[mainKpiOptionId.fact_table_id] ?? []
    const filteredOptions = attributeOptions.filter((option) =>
      validateAttributeOption(option, type, settings.type.selected)
    )

    if (settings.kpi_options.length === 1) {
      return filteredOptions
    }

    // get all available relation keys for each kpi option
    const segmentAttributeIds = settings.kpi_options.map((kpi) =>
      (optionsByFactTableId[kpi.fact_table_id] ?? [])
        .filter((attr) => attr[type])
        .map((option) => option.attribute_id)
    )

    // find all in common among kpi options
    const availableOptions = itemsIncludedInAllLists(segmentAttributeIds)

    // only return common options
    return filteredOptions.filter((opt) => opt.attribute_id in availableOptions)
  }
)

export const selectKpiOptionAttributeOptions = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) =>
      state.AttributeOptionStore.optionsByFactTableId,
    (_: ApplicationState, settings: TWidgetSettings) => settings,
    (
      _: ApplicationState,
      settings: TWidgetSettings,
      kpiOption: KpiOptionObject
    ) => kpiOption,
    (
      _: ApplicationState,
      settings: TWidgetSettings,
      kpiOption: KpiOptionObject,
      optionType: 'breakdown_attribute' | 'filter_attribute'
    ) => optionType
  ],
  (optionsByFactTableId, settings, kpiOption, optionType) => {
    const attributeOptions = optionsByFactTableId[kpiOption.fact_table_id] ?? []

    return attributeOptions.filter((option) =>
      validateAttributeOption(option, optionType, settings.type.selected)
    )
  }
)

export const selectMetricSegmentAttributeOptions = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) =>
      state.AttributeOptionStore.optionsByFactTableId,
    (_: ApplicationState, factTableId?: string | null) => factTableId
  ],
  (optionsByFactTableId, factTableId) => {
    if (!factTableId) {
      return []
    }

    const attributeOptions = optionsByFactTableId[factTableId] ?? []

    return attributeOptions.filter((option) => option.allow_metric_segment_by)
  }
)

export const selectAttributeOption = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.AttributeOptionStore.data,
    (_: ApplicationState, id?: string | null) => id
  ],
  (attributeOptions, id) => {
    if (!id) {
      return null
    }

    return attributeOptions[id] || null
  }
)

export const selectBenchmarkAttributeOptions = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.AttributeOptionStore.data,
    (_: ApplicationState, filterOptions: string[]) => filterOptions
  ],
  (attributeOptions, filterOptions) => {
    return filterOptions.map((id) => attributeOptions[id]).filter(Boolean)
  }
)

export const selectSegmentBy = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.AttributeOptionStore.data,
    (_: ApplicationState, settings: TWidgetSettings | null) => settings,
    (
      _: ApplicationState,
      settings: TWidgetSettings | null,
      customSegmentBy?: string | null
    ) => customSegmentBy
  ],
  (data, settings, customSegmentBy) => {
    if (customSegmentBy) {
      return data[customSegmentBy]
    }

    if (!settings || !settings.segment_by.selected) {
      return null
    }

    return data[settings.segment_by.selected]
  }
)

export const selectBreakdownBy = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.AttributeOptionStore.data,
    (_: ApplicationState, kpiOption: KpiOptionObject) => kpiOption
  ],
  (data, kpiOption) => {
    if (!kpiOption.breakdown_by.selected) {
      return null
    }

    return data[kpiOption.breakdown_by.selected]
  }
)
